import {
    Box,
    Button,
    CircularProgress,
    FormHelperText,
    IconButton,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {
    Form,
    Formik,
    FormikProps
} from "./Formik";
import OtpInput, { InputProps } from "react-otp-input";
import { MFSubmitButton } from ".";
import {
    useEffect,
    useRef,
    useState
} from "react";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface Values {
    otp: string;
}

const initialValues: Values = {
    otp: "",
};

const OTP_TIMER = 60

function OtpTimer({
    onResend,
    disabled,
    loading
}: {
    onResend: () => void;
    disabled: boolean;
    loading: boolean;
}): JSX.Element {
    const [counter, setCounter] = useState(OTP_TIMER);

    useEffect(() => {
        let timer: any = null

        if (counter > 0) {
            timer = setInterval(() => {
                setCounter(counter - 1);
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [counter]);

    const statusText = counter > 0 ? `Resend OTP available in ${counter} seconds` : "You can now resend OTP";

    const handleResend = async () => {
        try {
            if (counter === 0) {
                await onResend();
                setCounter(OTP_TIMER);
            }
        } catch (e) {
            console.error((e as Error).message);
        }
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Typography
                    variant='body_400'
                    color="textDark.secondary"
                >
                    Didn’t receive OTP ?
                </Typography>
                {loading ? (
                    <CircularProgress size={15} sx={{ marginLeft: "5px" }} />
                ) : (
                    <Button
                        variant="text"
                        sx={(theme: Theme) => ({
                            cursor: counter > 0 ? "not-allowed" : "pointer",
                            color: counter > 0 ? theme.palette.textDark.secondary : theme.palette.primary[500],
                            padding: "0px 5px",
                            mt: 2,
                            ...theme.typography.body_400
                        })}
                        onClick={handleResend}
                        disabled={disabled || counter > 0}
                    >
                        Resend OTP
                    </Button>
                )}
            </Box>
            <Box
                component='span'
                sx={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                    background: '20px solid red'
                }}
                aria-live="polite"
                aria-atomic="true"
            >
                {statusText}
            </Box>
            {counter > 0 && (
                <Typography
                    variant='body_400'
                    color="textDark.secondary"
                >
                    in {counter} seconds
                </Typography>
            )}
        </Box>
    );
}

export default function OTPForm({
    onSubmit,
    onResend,
    otpMessage,
    warningMessage = "Note: User Account will be locked after 3 unsuccessful attempts, Resend otp can be requested 2 times for every hour"
}: {
    onSubmit: (otp: string) => void;
    onResend: () => void;
    otpMessage: string;
    warningMessage?: string;

}): JSX.Element {
    const theme = useTheme()
    const isMd = useMediaQuery(theme.breakpoints.up('md'));
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const isXs = useMediaQuery(theme.breakpoints.up('xs'));
    const formikRef = useRef<FormikProps<Values>>(null);

    const [isLoading, setLoading] = useState<boolean>(false)
    const [isSubmitting, setSubmitting] = useState<boolean>(false)
    const [isPasswordVisible, setPasswordVisible] = useState<boolean>(false)

    const handleResendOtp = async () => {
        try {
            setLoading(true)
            await onResend()
        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setLoading(false)
        }
    };

    const handleChange = (otp: string) => {
        formikRef?.current?.setValues({ otp })
    }

    const handleSubmit = async (
        values: Values,
        { resetForm }: { resetForm: () => void }) => {
        try {
            setSubmitting(true)

            const { otp = "" } = values;

            if (!otp || otp?.length !== 6) {
                formikRef?.current?.setErrors({ otp: 'Enter OTP' })
                return;
            }

            await onSubmit(otp)
            resetForm()
        } catch (e) {
            console.error((e as Error).message);
            resetForm()
        } finally {
            setSubmitting(false)
        }
    }

    const togglePasswordVisibility = () => {
        setPasswordVisible(prevState => !prevState);
    }

    return (
        <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ values, errors }) => (
                <Form noValidate>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.primary[500],
                            textAlign: 'center',
                            marginBottom: "20px",
                        })}
                    >
                        {otpMessage ?? ""}
                    </Typography>
                    <Box
                        component="fieldset"
                        sx={{
                            mb: 0,
                            border: 'none',
                            p: 0,
                            m: 0,
                            '.otp-input': {
                                input: {
                                    width: { xs: '37px !important', md: '45px !important' },
                                    fontSize: { xs: '20px !important', md: '30px !important' },
                                    margin: { xs: '2px !important', md: '4px !important' },
                                },
                            },
                        }}>
                        <Typography
                            component="legend"
                            id="otp-input-group-label"
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary,
                                textAlign: 'center',
                                mb: 2,
                            })}
                        >
                            Enter OTP
                        </Typography>
                        <Box
                            role="group"
                            aria-labelledby="otp-input-group-label"
                            sx={{ display: 'flex', justifyContent: 'center' }}
                        >
                            <OtpInput
                                inputType='number'
                                renderInput={(inputProps: InputProps, index: number) => (
                                    <input
                                        {...inputProps}
                                        type={isPasswordVisible ? 'text' : 'password'}
                                        style={{ ...inputProps.style }}
                                        disabled={isLoading || isSubmitting}
                                        aria-label={`Enter OTP, digit ${index + 1} of 6`}
                                    />
                                )}
                                value={values.otp}
                                // isDisabled={isLoading || isSubmitting}
                                onChange={handleChange}
                                numInputs={6}
                                // hasErrored={!!errors?.otp}
                                // isInputNum={true}
                                // isInputSecure={true}
                                shouldAutoFocus={true}
                                containerStyle={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    // flexWrap: 'wrap',
                                }}
                                inputStyle={{
                                    width: !isSm ? '35px' : !isMd ? '40px' : '45px',
                                    height: !isSm ? '35px' : !isMd ? '40px' : '45px',
                                    padding: '5px 10px',
                                    borderRadius: '5px',
                                    fontSize: '30px',
                                    borderColor: errors?.otp ? `${theme.palette.error[900]}` : `${theme.palette.text.primary}`,
                                    color: errors?.otp ? `${theme.palette.error[900]}` : `${theme.palette.text.primary}`,
                                    outline: 'none'
                                }}
                                renderSeparator={() => (
                                    <span style={{ width: '6px' }}></span>
                                )}
                            // errorStyle={{
                            //     border: `2px solid ${theme.palette.error[500]}`,
                            //     color: theme.palette.error[500],
                            // }}
                            // focusStyle={{ outline: 'none' }}
                            // className="otp-input"
                            />
                        </Box>
                        {!!errors?.otp && (
                            <FormHelperText
                                error
                                sx={{
                                    mt: 2,
                                    textAlign: 'center',
                                    fontSize: '14px',
                                    color: `${theme.palette.error[900]}`,
                                    fontWeight: 500
                                }}
                            >
                                {errors?.otp}
                            </FormHelperText>
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginTop: "0.5rem",
                            }}
                        >
                            <IconButton
                                sx={{
                                    display: "flex",
                                    columnGap: "0.5rem",
                                    borderRadius: "1rem",
                                }}
                                onClick={togglePasswordVisibility}
                                disabled={isLoading || isSubmitting}
                            >
                                {isPasswordVisible ? <VisibilityOffIcon fontSize="small" sx={(theme: Theme) => ({ color: theme.palette.textDark.secondary })} /> : <VisibilityIcon fontSize="small" sx={(theme: Theme) => ({ color: theme.palette.textDark.secondary })} />}
                                <Typography
                                    variant='subtitle_600'
                                    sx={{
                                        color: theme.palette.textDark.secondary,
                                        fontWeight: 500
                                    }}
                                >
                                    {isPasswordVisible ? "Hide OTP" : "Show OTP"}
                                </Typography>
                            </IconButton>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            margin: "5px 0px 10px 0px",
                        }}
                    >
                        <OtpTimer
                            onResend={handleResendOtp}
                            disabled={isLoading || isSubmitting}
                            loading={isLoading}
                        />
                    </Box>
                    <Typography
                        variant="subtitle_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.error[500],
                            textAlign: 'center',
                            marginBottom: "20px",
                        })}>
                        {warningMessage}
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                        <MFSubmitButton
                            label="Submit"
                            disabled={isLoading}
                            loadingEnable={isSubmitting}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    )
}
